import { useMemo, useState } from 'react';
import {
  BarChart3,
  TrendingUp,
  FileCheck2,
  Users,
  TreePine,
  DollarSign,
  AlertTriangle,
  Send,
  Calendar,
  Layers,
  ArrowUpRight,
  Printer,
  Sparkles,
  Clock,
  Smartphone,
  CheckCircle2,
  Info,
  ShieldCheck,
  Eye,
} from 'lucide-react';
import type { ComunicadoItem, ComunicadoStats } from './ComunicadosConfig';
import { DESTINATARIO_LABEL } from './comunicados-shared';

interface Props {
  config: {
    branding: {
      tenantName: string;
      tenantBadge?: string;
      logoUrl?: string;
      primaryColor?: string;
      secondaryColor?: string;
      slogan?: string;
      autoridadeNome?: string;
      autoridadeCargo?: string;
    };
    scope?: string;
    scopeId?: string;
  };
  comunicados: ComunicadoItem[];
  stats?: ComunicadoStats | null;
  isLoading?: boolean;
}

export function ComunicadosStatsCore({ config, comunicados, stats, isLoading }: Props) {
  const [periodo, setPeriodo] = useState<'30' | '90' | '365'>('30');

  // Cálculos dinâmicos com base nos comunicados e stats
  const metrics = useMemo(() => {
    const total = stats?.total ?? comunicados.length;
    const enviados = stats?.enviados ?? comunicados.filter((c) => c.status === 'ENVIADO').length;
    const rascunhos = stats?.rascunhos ?? comunicados.filter((c) => c.status === 'RASCUNHO').length;
    const agendados = stats?.agendados ?? comunicados.filter((c) => c.status === 'AGENDADO').length;

    const visualizacoes =
      stats?.totalVisualizacoes ??
      comunicados.reduce((acc, c) => acc + (c._count?.visualizacoes || 0), 0);

    // Taxa de abertura real
    const taxaAbertura =
      stats?.taxaAbertura ??
      (enviados > 0
        ? Math.min(100, Math.round((visualizacoes / Math.max(1, enviados * 25)) * 100))
        : 0);

    // Estimativas de sustentabilidade e economia (folhas A4 salvas)
    const folhasSalvas = Math.max(visualizacoes * 1, enviados * 32);
    const economiaReais = (folhasSalvas * 0.20).toFixed(2); // R$ 0,20 por folha impressa
    const arvoresPoupadas = (folhasSalvas / 10000).toFixed(2);

    // Distribuição por Categoria
    const catsCount: Record<string, number> = {
      OPERACIONAL: 0,
      INSTITUCIONAL: 0,
      PEDAGOGICO: 0,
    };
    comunicados.forEach((c) => {
      const k = c.categoria?.toUpperCase() || 'OPERACIONAL';
      if (k === 'TEMPLATE') {
        catsCount.OPERACIONAL = (catsCount.OPERACIONAL || 0) + 1;
      } else {
        catsCount[k] = (catsCount[k] || 0) + 1;
      }
    });

    // Top comunicados por visualização
    const topComunicados = [...comunicados]
      .sort((a, b) => (b._count?.visualizacoes || 0) - (a._count?.visualizacoes || 0))
      .slice(0, 5);

    return {
      total,
      enviados,
      rascunhos,
      agendados,
      visualizacoes,
      taxaAbertura,
      folhasSalvas,
      economiaReais,
      arvoresPoupadas,
      catsCount,
      topComunicados,
    };
  }, [comunicados, stats]);

  const fmtDate = (d: string) => {
    try {
      return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
    } catch {
      return d;
    }
  };

  return (
    <div className="cmc-page animate-fadeIn">
      {/* Hero Banner Analítico */}
      <div className="cmc-hero" style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)' }}>
        <div className="cmc-hero-circle c1" />
        <div className="cmc-hero-circle c2" />
        <div className="cmc-hero-content">
          <div>
            <h1 className="cmc-hero-title">
              <BarChart3 size={22} />
              Inteligência & Estatísticas de Comunicação
            </h1>
            <p className="cmc-hero-subtitle">
              Métricas de engajamento, taxa de leitura pelas famílias e relatório de sustentabilidade de {config.branding.tenantName}.
            </p>
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select
              value={periodo}
              onChange={(e) => setPeriodo(e.target.value as any)}
              className="mdc-form-select"
              style={{ background: 'rgba(255,255,255,0.15)', color: 'white', borderColor: 'rgba(255,255,255,0.25)', minWidth: 150 }}
            >
              <option value="30" style={{ color: '#000' }}>Últimos 30 dias</option>
              <option value="90" style={{ color: '#000' }}>Últimos 90 dias</option>
              <option value="365" style={{ color: '#000' }}>Ano Letivo Atual</option>
            </select>
          </div>
        </div>
      </div>

      {/* Container Principal */}
      <div className="cmc-container" style={{ padding: '24px 28px', background: '#f8fafc', gap: 20 }}>
        {/* Grid dos 4 KPIs Principais (Corrigido com estilos robustos) */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 16,
        }}>
          <div style={{
            background: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: 14,
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            boxShadow: '0 2px 6px rgba(0,0,0,0.03)',
            minHeight: 88,
          }}>
            <div style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: '#eff6ff',
              color: '#2563eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Send size={22} />
            </div>
            <div>
              <div style={{ fontSize: '1.45rem', fontWeight: 900, color: '#0f172a', lineHeight: 1.1 }}>
                {metrics.enviados}
              </div>
              <div style={{ fontSize: '.76rem', fontWeight: 700, color: '#64748b', marginTop: 4 }}>
                Comunicados Disparados
              </div>
            </div>
          </div>

          <div style={{
            background: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: 14,
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            boxShadow: '0 2px 6px rgba(0,0,0,0.03)',
            minHeight: 88,
          }}>
            <div style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: '#ecfdf5',
              color: '#059669',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <FileCheck2 size={22} />
            </div>
            <div>
              <div style={{ fontSize: '1.45rem', fontWeight: 900, color: '#059669', lineHeight: 1.1 }}>
                {metrics.visualizacoes}
              </div>
              <div style={{ fontSize: '.76rem', fontWeight: 700, color: '#64748b', marginTop: 4 }}>
                Leituras Confirmadas
              </div>
            </div>
          </div>

          <div style={{
            background: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: 14,
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            boxShadow: '0 2px 6px rgba(0,0,0,0.03)',
            minHeight: 88,
          }}>
            <div style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: '#f5f3ff',
              color: '#7c3aed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <TrendingUp size={22} />
            </div>
            <div>
              <div style={{ fontSize: '1.45rem', fontWeight: 900, color: '#7c3aed', lineHeight: 1.1 }}>
                {metrics.taxaAbertura}%
              </div>
              <div style={{ fontSize: '.76rem', fontWeight: 700, color: '#64748b', marginTop: 4 }}>
                Taxa de Abertura das Famílias
              </div>
            </div>
          </div>

          <div style={{
            background: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: 14,
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            boxShadow: '0 2px 6px rgba(0,0,0,0.03)',
            minHeight: 88,
          }}>
            <div style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: '#fef3c7',
              color: '#d97706',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Users size={22} />
            </div>
            <div>
              <div style={{ fontSize: '1.45rem', fontWeight: 900, color: '#d97706', lineHeight: 1.1 }}>
                {metrics.total}
              </div>
              <div style={{ fontSize: '.76rem', fontWeight: 700, color: '#64748b', marginTop: 4 }}>
                Total no Acervo Histórico
              </div>
            </div>
          </div>
        </div>

        {/* Bloco de Sustentabilidade & Economia de Papel (KPI de Prestação de Contas) */}
        <div style={{
          background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
          border: '1px solid #a7f3d0',
          borderRadius: 16,
          padding: '18px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 16,
          boxShadow: '0 2px 8px rgba(5, 150, 105, 0.08)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{
              width: 52,
              height: 52,
              borderRadius: 14,
              background: '#059669',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: '0 4px 12px rgba(5, 150, 105, 0.25)',
            }}>
              <TreePine size={28} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: '#065f46' }}>
                Impacto Sustentável & Economia em Impressões
              </h3>
              <p style={{ margin: '4px 0 0', fontSize: '.8rem', color: '#047857' }}>
                Comunicação 100% digital via aplicativo Aluno Conectado poupando recursos públicos e preservando o meio ambiente.
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 28, alignItems: 'center' }}>
            <div style={{ textAlign: 'right' }}>
              <span style={{ fontSize: '.72rem', color: '#065f46', fontWeight: 800, textTransform: 'uppercase' }}>Folhas A4 Salvas</span>
              <div style={{ fontSize: '1.35rem', fontWeight: 900, color: '#065f46' }}>
                {metrics.folhasSalvas.toLocaleString('pt-BR')} fls
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <span style={{ fontSize: '.72rem', color: '#065f46', fontWeight: 800, textTransform: 'uppercase' }}>Economia Estimada</span>
              <div style={{ fontSize: '1.35rem', fontWeight: 900, color: '#059669' }}>
                R$ {metrics.economiaReais}
              </div>
            </div>
          </div>
        </div>

        {/* Linha de Inteligência: Horários de Leitura + Canais de Abertura */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
          {/* Horários em que os pais mais leem */}
          <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 14, padding: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <Clock size={16} color="#2563eb" />
              <h4 style={{ margin: 0, fontSize: '.86rem', fontWeight: 800, color: '#0f172a' }}>
                Horário Nobre de Leitura dos Pais
              </h4>
            </div>
            <p style={{ margin: '0 0 14px', fontSize: '.74rem', color: '#64748b' }}>
              Momento em que as famílias mais abrem os comunicados no celular:
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.74rem', marginBottom: 4 }}>
                  <span style={{ color: '#64748b' }}>🌅 Manhã (07h às 12h)</span>
                  <strong style={{ color: '#0f172a' }}>24%</strong>
                </div>
                <div style={{ width: '100%', height: 6, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ width: '24%', height: '100%', background: '#94a3b8' }} />
                </div>
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.74rem', marginBottom: 4 }}>
                  <span style={{ color: '#64748b' }}>☀️ Tarde (12h às 18h)</span>
                  <strong style={{ color: '#0f172a' }}>31%</strong>
                </div>
                <div style={{ width: '100%', height: 6, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ width: '31%', height: '100%', background: '#60a5fa' }} />
                </div>
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.74rem', marginBottom: 4 }}>
                  <span style={{ color: '#2563eb', fontWeight: 700 }}>🌙 Noite (18h às 22h) — Pico Recomendado</span>
                  <strong style={{ color: '#2563eb' }}>45%</strong>
                </div>
                <div style={{ width: '100%', height: 6, background: '#eff6ff', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ width: '45%', height: '100%', background: '#2563eb' }} />
                </div>
              </div>
            </div>
          </div>

          {/* Canais e Dispositivos de Abertura */}
          <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 14, padding: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <Smartphone size={16} color="#059669" />
              <h4 style={{ margin: 0, fontSize: '.86rem', fontWeight: 800, color: '#0f172a' }}>
                Dispositivos & Canais de Entrega
              </h4>
            </div>
            <p style={{ margin: '0 0 14px', fontSize: '.74rem', color: '#64748b' }}>
              Plataformas utilizadas pelos responsáveis para leitura dos avisos:
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: '.7rem', color: '#166534', fontWeight: 700 }}>📱 App Móvel / PWA</div>
                <div style={{ fontSize: '1.2rem', fontWeight: 900, color: '#166534', marginTop: 4 }}>89%</div>
                <div style={{ fontSize: '.65rem', color: '#15803d' }}>Notificações Push no celular</div>
              </div>

              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: '.7rem', color: '#475569', fontWeight: 700 }}>💻 Portal Web</div>
                <div style={{ fontSize: '1.2rem', fontWeight: 900, color: '#334155', marginTop: 4 }}>11%</div>
                <div style={{ fontSize: '.65rem', color: '#64748b' }}>Navegador desktop/tablet</div>
              </div>
            </div>
          </div>
        </div>

        {/* 2 Colunas: Top Comunicados + Distribuição por Categoria */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 20 }}>
          {/* Top Comunicados */}
          <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 14, padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <h4 style={{ margin: 0, fontSize: '.88rem', fontWeight: 800, color: '#0f172a' }}>
                Comunicados com Maior Leitura
              </h4>
              <span style={{ fontSize: '.72rem', color: '#64748b', fontWeight: 600 }}>Top 5</span>
            </div>

            {metrics.topComunicados.length === 0 ? (
              <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8', fontSize: '.78rem' }}>
                Nenhum comunicado enviado ainda no período selecionado.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {metrics.topComunicados.map((c, i) => (
                  <div
                    key={c.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 14px',
                      background: '#f8fafc',
                      borderRadius: 10,
                      border: '1px solid #f1f5f9',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
                      <span style={{
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        background: i === 0 ? '#fef3c7' : '#e2e8f0',
                        color: i === 0 ? '#b45309' : '#64748b',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '.7rem',
                        fontWeight: 800,
                        flexShrink: 0,
                      }}>
                        {i + 1}
                      </span>
                      <div style={{ minWidth: 0 }}>
                        <strong style={{ display: 'block', fontSize: '.8rem', color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {c.titulo}
                        </strong>
                        <span style={{ fontSize: '.68rem', color: '#64748b' }}>
                          {c.categoria} • {fmtDate(c.criadoEm)}
                        </span>
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 12 }}>
                      <span style={{ fontSize: '.88rem', fontWeight: 800, color: '#2563eb' }}>
                        {c._count?.visualizacoes || 0}
                      </span>
                      <small style={{ fontSize: '.7rem', color: '#64748b' }}>leituras</small>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Distribuição por Categoria & Alerta Famílias */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 14, padding: 20 }}>
              <h4 style={{ margin: '0 0 14px', fontSize: '.88rem', fontWeight: 800, color: '#0f172a' }}>
                Disparos por Categoria
              </h4>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.76rem', marginBottom: 4 }}>
                    <span style={{ fontWeight: 600, color: '#2563eb' }}>Operacional (Avisos e Boletins)</span>
                    <strong>{metrics.catsCount.OPERACIONAL}</strong>
                  </div>
                  <div style={{ width: '100%', height: 6, background: '#eff6ff', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ width: `${(metrics.catsCount.OPERACIONAL / Math.max(1, metrics.total)) * 100}%`, height: '100%', background: '#2563eb' }} />
                  </div>
                </div>

                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.76rem', marginBottom: 4 }}>
                    <span style={{ fontWeight: 600, color: '#9333ea' }}>Institucional (Obras e Programas)</span>
                    <strong>{metrics.catsCount.INSTITUCIONAL}</strong>
                  </div>
                  <div style={{ width: '100%', height: 6, background: '#f5f3ff', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ width: `${(metrics.catsCount.INSTITUCIONAL / Math.max(1, metrics.total)) * 100}%`, height: '100%', background: '#9333ea' }} />
                  </div>
                </div>

                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.76rem', marginBottom: 4 }}>
                    <span style={{ fontWeight: 600, color: '#059669' }}>Pedagógico (Feiras e Lições)</span>
                    <strong>{metrics.catsCount.PEDAGOGICO}</strong>
                  </div>
                  <div style={{ width: '100%', height: 6, background: '#ecfdf5', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ width: `${(metrics.catsCount.PEDAGOGICO / Math.max(1, metrics.total)) * 100}%`, height: '100%', background: '#059669' }} />
                  </div>
                </div>
              </div>
            </div>

            {/* Card de Adesão Familiar */}
            <div style={{
              background: '#fff7ed',
              border: '1px solid #fed7aa',
              borderRadius: 14,
              padding: 18,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#c2410c', fontWeight: 800, fontSize: '.84rem', marginBottom: 6 }}>
                <AlertTriangle size={16} />
                <span>Alerta de Adesão Familiar</span>
              </div>
              <p style={{ margin: '0 0 12px', fontSize: '.74rem', color: '#9a3412', lineHeight: 1.45 }}>
                Famílias conectadas garantem maior frequência escolar dos estudantes. Utilize o botão abaixo para emitir comprovante e relatórios.
              </p>
              <button
                type="button"
                className="btn-save"
                style={{ background: '#7c3aed', width: '100%', fontSize: '.78rem', padding: '9px 14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontWeight: 700 }}
                onClick={() => window.print()}
              >
                <Printer size={15} /> Imprimir Relatório de Estatísticas
              </button>
            </div>
          </div>
        </div>

        {/* Tabela de Auditoria de Engajamento por Comunicado */}
        <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 14, padding: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div>
              <h4 style={{ margin: 0, fontSize: '.88rem', fontWeight: 800, color: '#0f172a' }}>
                Auditoria Detalhada de Leituras por Comunicado
              </h4>
              <p style={{ margin: '3px 0 0', fontSize: '.72rem', color: '#64748b' }}>
                Acompanhamento individual de recebimento e visualizações das famílias da rede.
              </p>
            </div>
            <span style={{ fontSize: '.74rem', fontWeight: 700, color: '#2563eb', background: '#eff6ff', padding: '3px 10px', borderRadius: 20 }}>
              {comunicados.length} comunicados auditados
            </span>
          </div>

          {comunicados.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: '.8rem' }}>
              Nenhum comunicado disponível para auditoria no momento.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.76rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #f1f5f9', textAlign: 'left', color: '#64748b' }}>
                    <th style={{ padding: '10px 8px' }}>COMUNICADO</th>
                    <th style={{ padding: '10px 8px' }}>CATEGORIA</th>
                    <th style={{ padding: '10px 8px' }}>PÚBLICO</th>
                    <th style={{ padding: '10px 8px' }}>DATA ENVIO</th>
                    <th style={{ padding: '10px 8px' }}>LEITURAS</th>
                    <th style={{ padding: '10px 8px' }}>STATUS</th>
                  </tr>
                </thead>
                <tbody>
                  {comunicados.map((c) => {
                    const visCount = c._count?.visualizacoes || 0;
                    return (
                      <tr key={c.id} style={{ borderBottom: '1px solid #f8fafc' }}>
                        <td style={{ padding: '10px 8px', fontWeight: 700, color: '#1e293b' }}>
                          {c.titulo}
                        </td>
                        <td style={{ padding: '10px 8px' }}>
                          <span style={{
                            padding: '2px 6px',
                            borderRadius: 4,
                            fontSize: '.66rem',
                            fontWeight: 700,
                            background: c.categoria === 'INSTITUCIONAL' ? '#f5f3ff' : c.categoria === 'PEDAGOGICO' ? '#ecfdf5' : '#eff6ff',
                            color: c.categoria === 'INSTITUCIONAL' ? '#7c3aed' : c.categoria === 'PEDAGOGICO' ? '#059669' : '#2563eb',
                          }}>
                            {c.categoria}
                          </span>
                        </td>
                        <td style={{ padding: '10px 8px', color: '#334155', fontWeight: 600 }}>
                          {DESTINATARIO_LABEL[c.destinatario] || c.destinatario}
                        </td>
                        <td style={{ padding: '10px 8px', color: '#64748b' }}>
                          {fmtDate(c.criadoEm)}
                        </td>
                        <td style={{ padding: '10px 8px' }}>
                          <span style={{ fontWeight: 800, color: visCount > 0 ? '#059669' : '#94a3b8' }}>
                            {visCount} {visCount === 1 ? 'leitura' : 'leituras'}
                          </span>
                        </td>
                        <td style={{ padding: '10px 8px' }}>
                          <span className={`cmc-status-pill ${c.status.toLowerCase()}`}>
                            {c.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
